import { Button, Form, Image, Modal, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import { DeviceFunctions } from '../DeviceFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MapCard } from '@iotopen/lynx-react-components';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { faLocation } from '@fortawesome/free-solid-svg-icons';
import { useGlobalLynxClient } from '@iotopen/react-lynx';
import { LatLngExpression } from 'leaflet';
import { useGeolocation } from '../../hooks/useGeolocation';
import { isNullOrUndefined } from 'html5-qrcode/esm/core';

interface UpdateDeviceModalProps {
    selectedItem: string | null,
    showDeviceModal: boolean,
    setShowDeviceModal: Dispatch<SetStateAction<boolean>>,
    selectedDevice: ScannedItem | undefined,
    setSelectedDevice: Dispatch<SetStateAction<ScannedItem|undefined>>,
    name: string,
    setName: Dispatch<SetStateAction<string>>,
    comment: string,
    setComment: Dispatch<SetStateAction<string>>,
    onUpdateLocation: (selectedItem: string, latitude: number | null, longitude: number | null) => void,
    onUpdateDevice: (itemId: string, newDevice: ScannedItem) => Promise<void>, 
}

export const UpdateDeviceModal = ({
    selectedItem,
    showDeviceModal, 
    setShowDeviceModal,
    name,
    setName,
    comment,
    setComment, 
    selectedDevice,
    setSelectedDevice,
    onUpdateLocation,
    onUpdateDevice
}: UpdateDeviceModalProps) => {
    const [showLocationToast, setShowLocationToast] = useState(false);
    const {lynxClient} = useGlobalLynxClient();
    const {latitude, longitude} = useGeolocation();
    const fileRef = useRef<HTMLInputElement>(null);
    const [imageURL, setImageURL] = useState<string>('');
    const [downloadSuccessful, setDownloadSuccessful] = useState(false);
    const showImage = (downloadSuccessful && imageURL);
    
    const maxFileSize = 26214400; //25 megabytes

    const fallbackItem = {
        'id': 'Your Location',
        'meta': {
            'latitude': 62.17343589467479,
            'longitude': 14.941921234130861
        }
    };

    //download image to be displayed
    useEffect(() => {
        if(!selectedItem || !selectedDevice || !selectedDevice.meta['picture.hash']){
            setImageURL('');
            return;
        }
        if (selectedDevice.meta['picture.hash']?.length) {
            const pictureHash = selectedDevice?.meta['picture.hash'];
            lynxClient.downloadFile(pictureHash)
                .then(async (res) => {
                    const text = await res.text();
                    //XSS prevention DO NOT REMOVE THIS UNDER ANY CIRCUMSTANCE
                    if (!/script/i.test(text)) {
                        setDownloadSuccessful(true);
                        setImageURL(URL.createObjectURL(res));
                    } else {
                        alert('Something is not right with this image.\nIt will therefore not be displayed.');
                        setDownloadSuccessful(false);
                        setImageURL('');
                    }
                })
                .catch(() => {
                    console.error('could not download file with hash', selectedDevice.meta['picture.hash']);
                    setDownloadSuccessful(false);
                    setImageURL('');
                });
        } else {
            setImageURL('');
        }
    }, [lynxClient, imageURL, selectedDevice, selectedItem, downloadSuccessful]);

    useEffect(() => {
        if (selectedDevice?.meta) {
            setName(selectedDevice.meta.name || '');
            setComment(selectedDevice.meta.comment || '');
        }
    }, [selectedDevice, setName, setComment]);

    // Ensure we refresh device functions when the modal is opened
    useEffect(() => {
        if (showDeviceModal && selectedDevice && selectedDevice.id) {
            lynxClient.getFunctions(selectedDevice.installation_id, selectedDevice.id)
                .then(functions => {
                    if (functions && functions.length > 0) {
                        setSelectedDevice({...selectedDevice, functions: functions});
                    }
                })
                .catch(err => console.error('Failed to fetch device functions:', err));
        }
    }, [showDeviceModal, selectedDevice, lynxClient, setSelectedDevice]);

    const handleUpdateComment = (e: { target: { value: SetStateAction<string>; }; }) => {
        if(selectedDevice?.meta){
            const newComment = e.target.value as string;
            setSelectedDevice({...selectedDevice, meta: {...selectedDevice.meta, comment: newComment}});
            setComment(newComment);
        }
    };

    const handleUpdateName = (e: { target: { value: string; }; }) => {
        if(selectedDevice?.meta){
            const newName = e.target.value as string;
            setSelectedDevice({...selectedDevice, meta: {...selectedDevice.meta, name: newName}});
            setName(newName);
        }
    };

    const handleLocationClick = () => {
        if (latitude && longitude) {
            onUpdateLocation(selectedDevice.id.toString(), latitude, longitude);
            setSelectedDevice({...selectedDevice, meta: {...selectedDevice.meta, latitude: latitude.toString(), longitude: longitude.toString()}});
            setShowLocationToast(true);
        }
        else {
            alert('Location update failed.\n\nPlease make sure you have granted permission to use your location and try again.');
        }
    };

    const handleCancel = () => {
        setSelectedDevice(undefined);
        setDownloadSuccessful(false);
        setShowDeviceModal(false);
    };

    const handleUpdateDevice = (selectedDeviceId: number) => {
        setName(selectedDevice.meta.name);
        setComment(selectedDevice.meta.comment);
        setDownloadSuccessful(false);
        onUpdateDevice(selectedDeviceId.toString(), selectedDevice);

    };

    const handleFileSelect = () => {
        if(fileRef.current?.files?.length === 1) {
            const fileSize = fileRef.current.files[0].size;
            if(fileSize > maxFileSize){
                alert('File size is too large.\nThe maximum allowed file size is 25MB.');
            }
            else{
                lynxClient.createFileInstallation(selectedDevice.installation_id, fileRef.current.files[0])
                    .then( (res) => {
                        const newFile = [res].flat()[0];

                        setSelectedDevice({...selectedDevice, 
                            meta:{...selectedDevice.meta, 
                                'picture.id': String(newFile.id),
                                'picture.hash': newFile.hash 
                            }
                        });
                    })
                    .catch(e => console.error('Upload failed!', e));
            }
        }
        else{
            console.error('No file found');
        }
    };

    const getCenter = () => {
        if(selectedDevice?.meta.latitude && selectedDevice.meta.longitude){
            const currentLat = Number(selectedDevice.meta.latitude);
            const currentLng = Number(selectedDevice.meta.longitude);

            if(!isNaN(currentLat) && !isNaN(currentLng)){
                return [currentLat, currentLng] as LatLngExpression;
            }
        }
        if(isNullOrUndefined(latitude) || isNullOrUndefined(longitude)){
            return [fallbackItem.meta.latitude, fallbackItem.meta.longitude] as LatLngExpression;
        }
        return [latitude!, longitude!] as LatLngExpression;
    };

    if(!selectedDevice){
        return <Modal>
            <Modal.Header closeButton>
                <Modal.Title>Update Device</Modal.Title>
            </Modal.Header>
        </Modal>;
    }

    return <Modal show={showDeviceModal} onHide={handleCancel}>
        <Modal.Header closeButton>
            <Modal.Title>Update Device</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            {!selectedDevice ? <div className='d-flex flex-column align-items-center'><h4>Please wait...</h4><Spinner variant='primary'/></div> : 
                <Form onSubmit={(e) => {e.preventDefault(); handleUpdateDevice(selectedDevice.id);}}>
                    <Form.Group>
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={name}
                            onChange={handleUpdateName}
                            placeholder="Enter sensor name"
                        />
                    </Form.Group>
                    {selectedDevice && 
                            <>
                                <Form.Group className="mb-3 mt-3">
                                    <Form.Label>Device Info</Form.Label>
                                    <div className="border rounded p-3">
                                        {selectedDevice.meta?.eui && (
                                            <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                                <strong>DevEUI:</strong> {selectedDevice.meta.eui}
                                            </div>
                                        )}
                                        {selectedDevice.meta?.manufacturer && (
                                            <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                                <strong>Manufacturer:</strong> {selectedDevice.meta.manufacturer}
                                            </div>
                                        )}
                                        {selectedDevice.meta?.serial_nr && (
                                            <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                                <strong>Serial Number:</strong> {selectedDevice.meta.serial_nr}
                                            </div>
                                        )}
                                        {selectedDevice.meta?.last_updated && (
                                            <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                                <strong>Last Updated:</strong> {selectedDevice.meta.last_updated}
                                            </div>
                                        )}
                                        {Array.isArray(selectedDevice.functions) && selectedDevice.functions.length > 0 ? (
                                            <div className="mt-2">
                                                <DeviceFunctions functions={selectedDevice.functions}/>
                                            </div>
                                        ) : (
                                            <div className="text-muted mt-2" style={{ fontSize: '0.9rem' }}>
                                                <em>Radio signal information not available</em>
                                            </div>
                                        )}
                                    </div>
                                </Form.Group>
                            </>
                    }
                    <Form.Group className="mb-3">
                        <Form.Label>Comments</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            maxLength={512}
                            value={comment}
                            onChange={handleUpdateComment}
                            placeholder="Add comments about this device (max 512 characters)"
                        />
                        <Form.Text className="text-muted">
                            <small>Maximum 512 characters</small>
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className='my-2 justify-content-center'>
                        {showImage && <div className='d-flex justify-content-center'>
                            <Image thumbnail 
                                src={imageURL} 
                                alt={'device_image'}
                                className='bg-light'
                                style={{maxHeight: '10vh'}}
                            />
                        </div>}
                        <Form.Label>{imageURL.length ? null : 'Upload image file'}</Form.Label>
                        <Form.Control type="file" ref={fileRef} accept='image/*' onChange={handleFileSelect}/>
                        <Form.Text className="text-muted">
                            <small>Maximum file size: 25 MB</small>
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className='my-2 d-flex flex-column justify-content-center'>
                        <Form.Label>Location</Form.Label>
                        <MapCard 
                            mapItems={(selectedDevice.meta.latitude && selectedDevice.meta.longitude) ? [selectedDevice] : []} 
                            center={getCenter()} 
                            zoom={14} 
                            height='20vh'
                        />
                        <Button 
                            className='mt-2' 
                            onClick={handleLocationClick}
                        >
                                    Use Current Location <FontAwesomeIcon icon={faLocation}/>
                        </Button>
                    </Form.Group>
                </Form>}
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={handleCancel}>
                        Cancel
            </Button>
            <Button
                variant="primary"
                onClick={() => handleUpdateDevice(selectedDevice.id)}
                disabled={!selectedDevice}
            >
                        Update
            </Button>
        </Modal.Footer>
        <ToastContainer position="top-center" className="p-3">
            <Toast onClose={() => setShowLocationToast(false)} show={showLocationToast} delay={3000} autohide>
                <Toast.Header>
                    <strong className="me-auto">Location Updated</strong>
                </Toast.Header>
                <Toast.Body>Device location has been set to current position.</Toast.Body>
            </Toast>
        </ToastContainer>
    </Modal>;
};